import React, { useState } from 'react';

import { ActionIcon, Box, Stack, Text } from '@mantine/core';
import { IconHome, IconFolder, IconSettings, IconSearch, IconPlus, IconMicrophone, IconFileUpload, IconNotebook, IconEdit } from '@tabler/icons-react';
import { useNavigate } from 'react-router';
import { navigateToPathForReason } from '@/modules/navigation/navigation_module';
import { SettingsSheet } from './SettingsSheet';
import { useModule } from '@/hooks/useModule';
import { OpenUploadFileModalButton } from '@/modules/music_sniper/components/modals/upload_file_modal';

interface NavigationFooterProps {
    isHidden: boolean;
    onOpenSearch: () => void;
}

export const NavigationFooter = ({ isHidden, onOpenSearch }: NavigationFooterProps) => {
    const navigate = useNavigate();
    const [settingsOpen, setSettingsOpen] = useState(false);

    return (
        <>
            <Box
                className='mobile-footer'
                style={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 60,
                    display: 'flex',
                    backgroundColor: 'var(--mantine-color-body)',
                    borderTop: '1px solid var(--mantine-color-default-border)',
                    zIndex: 100,
                }}
            >
                <NavButton
                    icon={<IconHome size={24} />}
                    label="Home"
                    onClick={() => navigateToPathForReason(navigate, 'mobile_nav_home', '/dashboard')}
                />
                {/* <NavButton icon={<IconUpload size={24} />} label="Upload" /> */}
                <NavButton icon={<IconFolder size={24} />} label="Workspaces" />

                <CreateButton onClick={() => console.log('Create menu')} />

                <NavButton
                    icon={<IconSearch size={24} />}
                    label="Search"
                    onClick={onOpenSearch}
                />
                <NavButton
                    icon={<IconSettings size={24} />}
                    label="Settings"
                    onClick={() => setSettingsOpen(true)}
                />
            </Box>
            <SettingsSheet isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </>
    );
}

const CreateButton = ({ onClick }: { onClick?: () => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const audioPlayerModule = useModule('AudioPlayer');
    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();
    const isMiniPlayerVisible = Boolean(playbackStatus?.fileInfo);

    const handleToggle = () => {
        setIsOpen(!isOpen);
        onClick?.();
    };

    return (
        <Box
            style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                position: 'relative',
            }}
        >
            {isOpen && (
                <>
                    <Box
                        onClick={() => setIsOpen(false)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                            zIndex: 99,
                        }}
                    />
                    <Box
                        style={{
                            position: 'absolute',
                            bottom: isMiniPlayerVisible ? 140 : 80,
                            display: 'flex',
                            gap: 16,
                            zIndex: 101,
                        }}
                    >
                        <CreateMenuItem
                            icon={<IconMicrophone size={24} />}
                            onClick={() => {
                                console.log('Record');
                                setIsOpen(false);
                            }}
                        />
                        <OpenUploadFileModalButton component={({onClick}: {onClick: () => void}) => (
                            <CreateMenuItem
                                onClick={() => {
                                    setIsOpen(false);
                                    onClick();
                                }}
                                icon={<IconFileUpload size={24} />}
                            />
                        )} />
                        <CreateMenuItem
                            icon={<IconEdit size={24} />}
                            onClick={() => {
                                console.log('Create Project');
                                setIsOpen(false);
                            }}
                        />
                    </Box>
                </>
            )}
            <Box
                onClick={handleToggle}
                style={{
                    position: 'absolute',
                    top: 8,
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, var(--mantine-color-blue-6), var(--mantine-color-cyan-6))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    transition: 'transform 0.2s ease',
                    zIndex: 101,
                }}
            >
                <IconPlus size={22} stroke={2.5} color="white" />
            </Box>
        </Box>
    );
}

const CreateMenuItem = ({
    icon,
    onClick
}: {
    icon: React.ReactNode;
    onClick: () => void;
}) => {
    return (
        <Box
            onClick={onClick}
            style={{
                cursor: 'pointer',
                animation: 'slideUp 0.2s ease',
            }}
        >
            <Box
                style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: 'var(--mantine-color-body)',
                    border: '1px solid var(--mantine-color-default-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease',
                }}
                onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'scale(0.95)';
                }}
                onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                }}
            >
                {icon}
            </Box>
        </Box>
    );
}

const NavButton = ({
    icon,
    label,
    onClick,
}: {
    icon: React.ReactNode;
    label: string;
    onClick?: () => void;
}) => {
    return (
        <Box
            onClick={onClick}
            style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                userSelect: 'none',
            }}
        >
            <Stack gap={4} align="center">
                <ActionIcon variant="transparent" size="lg">
                    {icon}
                </ActionIcon>
                <Text size="xs" c="dimmed">
                    {label}
                </Text>
            </Stack>
        </Box>
    );
}
